import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AlertCircle,
  Check,
  Circle,
  Database,
  FileText,
  ListChecks,
  Target,
  TriangleAlert,
  Users,
  Blocks,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { getPlan, getPlanAtRevision } from '../../services/planApi';
import type { PlanState } from '../../services/planApi';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ScrollArea, ScrollAreaContent, ScrollAreaViewport, ScrollBar } from '../ui/scroll-area';

type Props = {
  appId: string;
  tenantId: string;
  appName: string;
  onWorkflow: (available: boolean, revision: number | null) => void;
};

type SectionId =
  | 'overview'
  | 'goal-users'
  | 'capabilities'
  | 'implementation'
  | 'discovery'
  | 'assumptions'
  | 'questions';
type SectionItem = { id: SectionId; label: string; icon: LucideIcon };

const sections: SectionItem[] = [
  { id: 'overview', label: 'Overview', icon: FileText },
  { id: 'goal-users', label: 'Goal and users', icon: Target },
  { id: 'capabilities', label: 'Capabilities & workflows', icon: Blocks },
  { id: 'implementation', label: 'Implementation steps', icon: ListChecks },
  { id: 'discovery', label: 'Discovery findings', icon: Database },
  { id: 'assumptions', label: 'Assumptions & out of scope', icon: TriangleAlert },
  { id: 'questions', label: 'Blocking questions', icon: Circle },
];

function ReadOnlyText({ value, empty = 'Not added yet.' }: { value: string; empty?: string }) {
  return value.trim() ? (
    <p className="whitespace-pre-wrap text-sm leading-5 text-app-text-secondary">{value}</p>
  ) : (
    <p className="text-sm italic text-app-text-muted">{empty}</p>
  );
}

function ReadOnlyList({
  items,
  empty = 'Nothing added yet.',
}: {
  items: string[];
  empty?: string;
}) {
  const values = items.map((item) => item.trim()).filter(Boolean);
  return values.length ? (
    <div className="grid gap-2">
      {values.map((item, index) => (
        <div
          key={`${item}-${index}`}
          className="rounded-lg border border-app-border bg-app-surface-muted/30 px-3 py-2 text-sm leading-5 text-app-text-secondary"
        >
          {item}
        </div>
      ))}
    </div>
  ) : (
    <p className="text-sm italic text-app-text-muted">{empty}</p>
  );
}

function PlanSection({
  id,
  number,
  title,
  icon: Icon,
  children,
}: {
  id: SectionId;
  number: number;
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <section
      id={`plan-section-${id}`}
      aria-labelledby={`plan-heading-${id}`}
      className="scroll-mt-3 border-b border-app-border px-4 py-4 last:border-b-0 sm:px-5"
    >
      <div className="mb-2 flex items-center gap-3">
        <Badge
          variant="secondary"
          className="size-7 justify-center rounded-full bg-app-brand-subtle p-0 font-semibold text-app-brand-strong"
        >
          {number}
        </Badge>
        <Icon className="size-5 shrink-0 text-app-brand-strong" aria-hidden="true" />
        <h2 id={`plan-heading-${id}`} className="text-base font-semibold text-app-text">
          {title}
        </h2>
      </div>
      <div className="min-w-0 pl-0 sm:pl-10">{children}</div>
    </section>
  );
}

export default function PlanWorkspace({ appId, tenantId, appName, onWorkflow }: Props) {
  const [state, setState] = useState<PlanState>();
  const [error, setError] = useState('');
  const loadController = useRef<AbortController | null>(null);

  const load = useCallback(
    async (minimumRevision?: number) => {
      loadController.current?.abort();
      const controller = new AbortController();
      loadController.current = controller;
      setError('');
      try {
        const latest =
          minimumRevision === undefined
            ? await getPlan(tenantId, appId, controller.signal)
            : await getPlanAtRevision(tenantId, appId, minimumRevision, controller.signal);
        if (controller.signal.aborted) return;
        if (minimumRevision !== undefined && (latest.revision ?? 0) < minimumRevision) return;
        setState(latest);
        onWorkflow(latest.plan !== null, latest.revision);
      } catch {
        if (!controller.signal.aborted) setError('Could not load the app plan.');
      }
    },
    [appId, onWorkflow, tenantId]
  );

  useEffect(() => {
    void load();
    return () => loadController.current?.abort();
  }, [load]);
  useEffect(() => {
    const onPlanUpdated = (event: Event) => {
      const detail = (event as CustomEvent<{ appId?: unknown; revision?: unknown }>).detail;
      if (detail?.appId === appId && typeof detail.revision === 'number') {
        void load(detail.revision);
      }
    };
    window.addEventListener('fabrik:app-plan-updated', onPlanUpdated);
    return () => window.removeEventListener('fabrik:app-plan-updated', onPlanUpdated);
  }, [appId, load]);

  if (!state) {
    return (
      <div
        className="flex min-h-0 flex-1 items-center justify-center p-6"
        role="status"
        aria-live="polite"
      >
        {error ? (
          <div className="grid justify-items-center gap-3 text-center">
            <AlertCircle className="size-6 text-destructive" aria-hidden="true" />
            <p className="text-sm text-app-text-secondary">{error}</p>
            <Button type="button" variant="outline" onClick={() => void load()}>
              Reload plan
            </Button>
          </div>
        ) : (
          <p className="text-sm text-app-text-muted">Loading plan…</p>
        )}
      </div>
    );
  }

  if (!state.plan) return null;

  const plan = state.plan;
  const implementationComplete =
    plan.implementationSteps.length > 0 && plan.implementationSteps.every((step) => step.completed);
  const phaseLabel =
    state.phase === 'discovery'
      ? 'Discovery'
      : state.phase === 'implementation'
      ? implementationComplete
        ? 'Completed'
        : 'Implementation'
      : 'Planning';
  const visibleSections = sections.filter(
    (item) =>
      item.id !== 'questions' ||
      plan.blockingQuestions.length > 0 ||
      state.blockers.includes('blockingQuestions')
  );
  const scrollToSection = (id: SectionId) =>
    document
      .getElementById(`plan-section-${id}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  return (
    <section
      className="@container flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-app-surface-muted p-3 sm:p-5"
      aria-label="Plan workspace"
    >
      <header className="mb-3 flex shrink-0 flex-wrap items-start justify-between gap-3 px-1">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-app-text">App plan</h1>
            <Badge variant="secondary" className="bg-app-brand-subtle text-app-brand-strong">
              {implementationComplete && state.phase === 'implementation' && (
                <Check className="mr-1 size-3.5" aria-hidden="true" />
              )}
              {phaseLabel}
            </Badge>
          </div>
          <p className="mt-1 max-w-4xl text-sm leading-5 text-app-text-muted">
            The assistant maintains this plan. Ask in chat to refine its purpose, workflows, scope,
            or implementation steps.
          </p>
        </div>
        <p className="shrink-0 text-right text-xs text-app-text-muted">
          Revision {state.revision ?? '—'}
        </p>
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden @7xl:flex-row">
        <Card className="hidden min-h-0 min-w-0 bg-app-surface-card @7xl:flex @7xl:w-56 @7xl:shrink-0">
          <CardHeader className="py-3">
            <CardTitle className="text-sm font-medium">Plan sections</CardTitle>
          </CardHeader>
          <CardContent className="grid content-start gap-1 overflow-y-auto px-3 pb-3">
            {visibleSections.map(({ id, label, icon: Icon }) => (
              <Button
                key={id}
                type="button"
                variant="ghost"
                className="h-auto min-h-9 justify-start whitespace-normal px-2.5 py-2 text-left text-sm text-app-text-secondary"
                onClick={() => scrollToSection(id)}
              >
                <Icon className="mr-1 size-4 shrink-0 text-app-text-muted" aria-hidden="true" />
                {label}
              </Button>
            ))}
          </CardContent>
        </Card>

        <Card className="min-h-0 min-w-0 flex-1 bg-app-surface-card @7xl:overflow-hidden">
          <CardContent className="flex min-h-0 flex-1 flex-col p-0">
            <ScrollArea className="min-h-0 min-w-0 flex-1">
              <ScrollAreaViewport className="overscroll-contain">
                <ScrollAreaContent className="w-full min-w-0! max-w-full">
                  <div
                    id="plan-section-overview"
                    className="flex scroll-mt-3 items-start gap-4 border-b border-app-border px-4 py-4 sm:px-5"
                  >
                    <div className="flex size-14 shrink-0 items-center justify-center rounded-lg border border-app-brand/30 bg-app-brand-subtle text-app-brand-strong">
                      <Users className="size-7" aria-hidden="true" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <h2 className="text-xl font-bold tracking-tight text-app-text">{appName}</h2>
                      <div className="mt-1">
                        <ReadOnlyText value={plan.overview} empty="Add an overview for this app." />
                      </div>
                    </div>
                  </div>

                  <PlanSection
                    id="goal-users"
                    number={1}
                    title="Goal and intended users"
                    icon={Target}
                  >
                    <ReadOnlyText value={plan.goalUsers} />
                  </PlanSection>

                  <PlanSection
                    id="capabilities"
                    number={2}
                    title="Capabilities and workflows"
                    icon={Blocks}
                  >
                    <ul className="grid gap-2 sm:grid-cols-2">
                      {plan.capabilitiesWorkflows.length ? (
                        plan.capabilitiesWorkflows.map((capability, index) => (
                          <li key={`${index}-${capability.slice(0, 16)}`}>
                            <Card
                              size="sm"
                              className="h-full border border-app-border bg-app-surface-muted/30 shadow-none"
                            >
                              <CardContent className="flex items-start gap-3 p-3">
                                <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-app-brand-subtle text-app-brand-strong">
                                  <FileText className="size-4" aria-hidden="true" />
                                </span>
                                <p className="min-w-0 whitespace-pre-wrap text-sm leading-5 text-app-text-secondary">
                                  {capability}
                                </p>
                              </CardContent>
                            </Card>
                          </li>
                        ))
                      ) : (
                        <li className="text-sm italic text-app-text-muted">
                          No capabilities added yet.
                        </li>
                      )}
                    </ul>
                  </PlanSection>

                  <PlanSection
                    id="implementation"
                    number={3}
                    title="Implementation steps"
                    icon={ListChecks}
                  >
                    <ol className="relative grid gap-3 before:absolute before:bottom-3.5 before:left-5 before:top-3.5 before:w-px before:bg-app-border">
                      {plan.implementationSteps.length ? (
                        plan.implementationSteps.map((step, index) => (
                          <li
                            key={`${index}-${step.title}`}
                            className="relative flex items-start gap-3"
                          >
                            <Badge
                              variant="secondary"
                              className="z-10 h-7 min-w-10 justify-center rounded-full bg-app-brand-subtle text-app-brand-strong"
                            >
                              S{index + 1}
                            </Badge>
                            <div className="min-w-0 flex-1 pt-0.5">
                              <p className="font-semibold leading-5 text-app-text">
                                {step.title || `Step ${index + 1}`}
                              </p>
                              {step.acceptanceCriteria.length ? (
                                <div className="mt-0.5 grid gap-0.5 text-sm leading-5 text-app-text-secondary">
                                  {step.acceptanceCriteria.map((criterion, criterionIndex) => (
                                    <p key={`${criterionIndex}-${criterion}`}>{criterion}</p>
                                  ))}
                                </div>
                              ) : (
                                <p className="mt-0.5 text-sm italic leading-5 text-app-text-muted">
                                  No acceptance criteria added.
                                </p>
                              )}
                            </div>
                          </li>
                        ))
                      ) : (
                        <li className="text-sm italic text-app-text-muted">
                          No implementation steps added yet.
                        </li>
                      )}
                    </ol>
                  </PlanSection>

                  <PlanSection
                    id="discovery"
                    number={4}
                    title="Discovery scopes and findings"
                    icon={Database}
                  >
                    <div className="grid gap-3">
                      {plan.discoveryScopes?.length ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          {plan.discoveryScopes.map((scope, index) => (
                            <Card
                              key={`${scope.systemId}-${scope.entity}-${index}`}
                              size="sm"
                              className="border border-app-border bg-app-surface-muted/30 shadow-none"
                            >
                              <CardContent className="grid gap-1 p-3 text-sm">
                                <p className="font-medium text-app-text">
                                  {scope.entity} · {scope.systemId}
                                </p>
                                <p className="text-app-text-secondary">{scope.purpose}</p>
                                {scope.allowedFields?.length ? (
                                  <p className="text-xs text-app-text-muted">
                                    Fields: {scope.allowedFields.join(', ')}
                                  </p>
                                ) : null}
                                {scope.filter && (
                                  <p className="text-xs text-app-text-muted">
                                    Filter: {scope.filter}
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm italic text-app-text-muted">
                          Discovery scopes will be added before research begins.
                        </p>
                      )}
                      <h3 className="text-sm font-semibold text-app-text">Findings</h3>
                      {plan.discoveryFindings.length ? (
                        plan.discoveryFindings.map((finding, index) => (
                          <Card
                            key={`${index}-${finding.source}`}
                            size="sm"
                            className="border border-app-border bg-app-surface-muted/30 shadow-none"
                          >
                            <CardContent className="grid gap-1 p-3">
                              <p className="whitespace-pre-wrap text-sm text-app-text-secondary">
                                {finding.finding}
                              </p>
                              <p className="text-xs text-app-text-muted">
                                {finding.source}
                                {finding.mapping ? ` · ${finding.mapping}` : ''}
                              </p>
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <p className="text-sm italic text-app-text-muted">
                          No findings recorded yet.
                        </p>
                      )}
                    </div>
                  </PlanSection>

                  <PlanSection
                    id="assumptions"
                    number={5}
                    title="Assumptions and out of scope"
                    icon={TriangleAlert}
                  >
                    <div className="grid gap-3 md:grid-cols-2">
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-app-text">Assumptions</h3>
                        <ReadOnlyList items={plan.assumptions} />
                      </div>
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-app-text">Out of scope</h3>
                        <ReadOnlyList items={plan.outOfScope} />
                      </div>
                    </div>
                  </PlanSection>

                  {visibleSections.some((item) => item.id === 'questions') && (
                    <PlanSection id="questions" number={6} title="Blocking questions" icon={Circle}>
                      <ReadOnlyList items={plan.blockingQuestions} />
                    </PlanSection>
                  )}
                </ScrollAreaContent>
              </ScrollAreaViewport>
              <ScrollBar />
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
